/**
 * Product card hover gallery (desktop).
 *
 * Hovering a card flash-swaps it to its second image (the theme's standard
 * hover swap), and arrow controls let the customer browse the
 * rest of the product's images from there. Works with the markup in
 * snippets/product-item.liquid and the styles in
 * assets/product-card-gallery.css. Uses event delegation so it keeps working
 * when product grids are re-rendered by AJAX (collection filtering,
 * recommendations, predictive search...).
 */
(function () {
  'use strict';

  // Touch devices keep the standard single-image card
  if (window.matchMedia && window.matchMedia('(-moz-touch-enabled: 1), (hover: none)').matches) {
    return;
  }

  function closest(element, selector) {
    if (!element || !element.closest) {
      return null;
    }

    return element.closest(selector);
  }

  function getImages(gallery) {
    return Array.prototype.slice.call(gallery.querySelectorAll('.ProductItem__Image[data-gallery-index]'));
  }

  /*
   * Force-load the extra images the first time the customer hovers the card.
   * They are kept visibility: hidden (and therefore skipped by lazySizes,
   * which runs with loadHidden: false) until then.
   */
  function primeGallery(gallery) {
    if (gallery.classList.contains('is-primed')) {
      return;
    }

    gallery.classList.add('is-primed');

    getImages(gallery).forEach(function (image) {
      if (image.classList.contains('Image--lazyLoad') && window.lazySizes && window.lazySizes.loader) {
        window.lazySizes.loader.unveil(image);
      }
    });
  }

  function setActiveImage(gallery, index) {
    var images = getImages(gallery);

    if (images.length === 0) {
      return;
    }

    var count = images.reduce(function (max, image) {
      return Math.max(max, parseInt(image.getAttribute('data-gallery-index'), 10));
    }, 0) + 1;

    index = ((index % count) + count) % count;

    // Only the shown image is opaque; the outgoing image fades out first and
    // the incoming one fades in a beat later (see the CSS), so the card
    // background blinks through as a light flash between them
    images.forEach(function (image) {
      var isShown = parseInt(image.getAttribute('data-gallery-index'), 10) === index;

      image.classList.toggle('is-active', isShown);
      image.style.setProperty('opacity', isShown ? '1' : '0', 'important');
    });

    gallery.setAttribute('data-gallery-current', index);
  }

  /*
   * Pin the arrow overlay to the photo as it is actually painted. Section
   * styles can make the image narrower than the card (scaled product lists,
   * natural-size images, object-fit letterboxing...), and an overlay spanning
   * the whole card would then leave the arrows floating beside the photo.
   */
  function fitNav(gallery) {
    var nav = gallery.querySelector('.ProductItemGallery__Nav');
    var image = gallery.querySelector('.ProductItem__Image[data-gallery-index="0"]');

    if (!nav || !image) {
      return;
    }

    var box = image.getBoundingClientRect();
    var galleryBox = gallery.getBoundingClientRect();

    if (box.width === 0 || box.height === 0) {
      return;
    }

    var left = box.left;
    var top = box.top;
    var width = box.width;
    var height = box.height;

    // Letterboxed images only paint part of their box
    var fit = window.getComputedStyle(image).objectFit;
    var ratio = (image.naturalWidth && image.naturalHeight)
      ? image.naturalWidth / image.naturalHeight
      : parseFloat(image.getAttribute('width')) / parseFloat(image.getAttribute('height'));

    if ((fit === 'contain' || fit === 'scale-down') && ratio > 0) {
      if (width / height > ratio) {
        var paintedWidth = height * ratio;
        left += (width - paintedWidth) / 2;
        width = paintedWidth;
      } else {
        var paintedHeight = width / ratio;
        top += (height - paintedHeight) / 2;
        height = paintedHeight;
      }
    }

    nav.style.left = (left - galleryBox.left) + 'px';
    nav.style.top = (top - galleryBox.top) + 'px';
    nav.style.width = width + 'px';
    nav.style.height = height + 'px';
    nav.style.right = 'auto';
    nav.style.bottom = 'auto';
  }

  document.addEventListener('mouseover', function (event) {
    var gallery = closest(event.target, '.ProductItemGallery');

    if (!gallery) {
      return;
    }

    primeGallery(gallery);

    // Moving between elements inside the card is not an entry
    if (event.relatedTarget && gallery.contains(event.relatedTarget)) {
      return;
    }

    fitNav(gallery);

    /*
     * Entering the card swaps it to its second image, like the theme's
     * standard hover swap. The arrows and dots navigate from there, and
     * leaving the card still resets it to the first image.
     */
    if ((parseInt(gallery.getAttribute('data-gallery-current'), 10) || 0) === 0) {
      setActiveImage(gallery, 1);
    }
  });

  document.addEventListener('click', function (event) {
    var control = closest(event.target, '.ProductItemGallery__Arrow');

    if (!control) {
      return;
    }

    var gallery = closest(control, '.ProductItemGallery');

    if (!gallery) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    primeGallery(gallery);

    var current = parseInt(gallery.getAttribute('data-gallery-current'), 10) || 0;

    if (control.hasAttribute('data-gallery-prev')) {
      setActiveImage(gallery, current - 1);
    } else {
      setActiveImage(gallery, current + 1);
    }

    /*
     * Mouse clicks leave focus on the control, which would keep the overlay
     * visible (:focus-within) after the pointer leaves the card. Keyboard
     * activation (event.detail === 0) keeps focus so tabbing still works.
     */
    if (event.detail > 0) {
      control.blur();
    }
  });

  // Keyboard users reach the arrows without hovering the card first
  document.addEventListener('focusin', function (event) {
    var gallery = closest(event.target, '.ProductItemGallery');

    if (gallery && closest(event.target, '.ProductItemGallery__Nav')) {
      fitNav(gallery);
    }
  });

  // Reset to the featured image when the mouse leaves the card
  document.addEventListener('mouseout', function (event) {
    var gallery = closest(event.target, '.ProductItemGallery');

    if (!gallery) {
      return;
    }

    if (event.relatedTarget && gallery.contains(event.relatedTarget)) {
      return;
    }

    if ((parseInt(gallery.getAttribute('data-gallery-current'), 10) || 0) !== 0) {
      setActiveImage(gallery, 0);
    }
  });
}());
